import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Paper,
  TextField,
  Alert,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import useApiEp from "../../hooks/useApiEp";
import PhoneIcon from "@mui/icons-material/Phone";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import { Link, useParams } from "react-router-dom";
import {
  getHeader,
  launchEsSignup,
  roundToMaxDecimals,
  saveToken,
  toastError,
} from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import FacebookIcon from "@mui/icons-material/Facebook";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SecurityIcon from "@mui/icons-material/Security";
import LinkIcon from "@mui/icons-material/Link";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import MigrateChatBot from "./MigrateChatBot";
import PlanDisplay from "./PlanDisplay";
import { toast } from "react-toastify";
import { GlassButton, GlassButtonLoading } from "../../themes/UiElements";
import LoginIcon from "@mui/icons-material/Login";
const AccountDetails = () => {
  const { id } = useParams();
  const [marking, setMarking] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const { data, isLoading, isError, error, refetch } = useApiEp(
    `/root/get-account/${id}`
  );

  const [openRCSModal, setOpenRCSModal] = useState(false);
  const [rcsUserId, setRcsUserId] = useState("");
  const [rcsApiKey, setRcsApiKey] = useState("");
  const [rcsBotId, setRcsBotId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleAccountSettings = useCallback(
    async (aStatus) => {
      if (!data) return;
      const payload = {};
      if (aStatus === "expiry") {
        payload.expired = !Boolean(data?.expired);
      } else if (aStatus === "mmLite") {
        payload.isMMLite = !Boolean(data?.isMMLite);
      }
      try {
        setMarking(true);
        const response = await axios.post(
          `${API_ENDPOINT}/root/account-settings`,
          { settings: payload, id: id },
          getHeader()
        );
        toast.success(response.data.message);
        refetch();
      } catch (error) {
        toastError(error);
      } finally {
        setMarking(false);
      }
    },
    [id, data, refetch]
  );

  const handleAccountLogin = useCallback(async () => {
    if (!data) {
      return;
    }
    try {
      setLoggingIn(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/account-login`,
        { customerId: data.customerId },
        getHeader()
      );
      saveToken(response.data.token);
      document.location.href = "/home";
    } catch (error) {
      toastError(error);
    } finally {
      setLoggingIn(false);
    }
  }, [data]);

  const handleOpenRCSModal = useCallback(() => {
    setRcsUserId(data?.rcsOptionBUserId || "");
    setRcsApiKey(data?.rcsOptionBapiKey || "");
    setRcsBotId(data?.rcsBotId || "");
    setOpenRCSModal(true);
  }, [data]);

  const handleRCSSubmit = useCallback(async () => {
    if (!rcsUserId || !rcsApiKey || !rcsBotId) {
      toastError("User ID, API Key, and Bot ID are required");
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_ENDPOINT}/root/enable-rcs-option2`,
        {
          userId: rcsUserId,
          apiKey: rcsApiKey,
          botId: rcsBotId,
          accountId: id,
        },
        getHeader()
      );
      toast.success(
        response.data.message || "RCS Option 2 configured successfully"
      );
      setOpenRCSModal(false);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  }, [rcsUserId, rcsApiKey, rcsBotId, id, refetch]);

  if (isLoading) {
    return (
      <Container sx={{ my: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ my: 3 }}>
        <Typography color="error">
          Error: {error?.message || "Failed to load account details"}
        </Typography>
      </Container>
    );
  }

  const account = data;

  // Grouped sections
  const InfoSection = ({ title, icon, children, ...props }) => (
    <Box {...props}>
      <Typography
        variant="h6"
        sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
      >
        {icon}
        {title}
      </Typography>
      {children}
    </Box>
  );

  const getQualityColor = (quality) => {
    switch (quality) {
      case "HIGH":
      case "GREEN":
        return "success";
      case "MEDIUM":
      case "YELLOW":
        return "warning";
      case "LOW":
      case "RED":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />
      <Container maxWidth="lg">
        <PageTitle sx={{ mt: 2, mb: 1 }}>Account Details</PageTitle>
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <PlanDisplay data={account} id={id} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "flex-end" }}
                >
                  <Chip
                    label={account.status.toUpperCase()}
                    color={account.status === "active" ? "success" : "error"}
                    variant="filled"
                    size="medium"
                  />
                  <GlassButtonLoading
                    loading={marking}
                    onClick={() => {
                      handleAccountSettings("expiry");
                    }}
                    variant={account?.expired ? "outlined" : "contained"}
                    color={account?.expired ? "success" : "error"}
                    size="small"
                  >
                    {account?.expired ? `Revoke Expiry` : `Mark Expired`}
                  </GlassButtonLoading>
                  <GlassButtonLoading
                    loading={marking}
                    onClick={() => {
                      handleAccountSettings("mmLite");
                    }}
                    variant={account?.isMMLite ? "outlined" : "contained"}
                    color={account?.isMMLite ? "success" : "error"}
                    size="small"
                  >
                    {account?.isMMLite ? `Unmark MM Lite?` : `Mark MM Lite`}
                  </GlassButtonLoading>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <Grid container spacing={3}>
          {/* Left Column - Account Information */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Customer & Basic Info */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <InfoSection
                    title="Customer Information"
                    icon={<PersonIcon />}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Customer ID
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {account.customerId}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Verified Name
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {account.verifiedName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Contact Number
                        </Typography>
                        <Typography variant="body1">
                          {account.contactNumber || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Email ID
                        </Typography>
                        <Typography variant="body1">
                          {account.contactEmail || "NA"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Created On
                        </Typography>
                        <Typography variant="body1">
                          {new Date(account.createdOn).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Users Count
                        </Typography>
                        <Link
                          to={`/home/${account.customerId}/users-list`}
                          style={{ textDecoration: "none" }}
                        >
                          <Chip
                            label={account.usersCount}
                            clickable
                            variant="outlined"
                            color="primary"
                          />
                        </Link>
                        <GlassButtonLoading
                          loading={loggingIn}
                          startIcon={<LoginIcon />}
                          sx={{ ml: 2 }}
                          onClick={handleAccountLogin}
                        >
                          Account Login
                        </GlassButtonLoading>
                      </Grid>
                    </Grid>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* WhatsApp Business Account Details */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <InfoSection
                    title="WhatsApp Business Account"
                    icon={<BusinessIcon />}
                  >
                    <Grid container spacing={2}>
                      {/* Phone Number ID */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Phone Number ID
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {account.phoneNumberId || "—"}
                        </Typography>
                      </Grid>

                      {/* WABA Account ID */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          WABA Account ID
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {account.wabaAccountId || "—"}
                        </Typography>
                      </Grid>

                      {/* App ID */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          App ID
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {account.appId || "—"}
                        </Typography>
                      </Grid>

                      {/* WABA Quality */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          WABA Quality
                        </Typography>
                        <Chip
                          label={account.wabaQuality || "UNKNOWN"}
                          color={getQualityColor(account.wabaQuality)}
                          size="small"
                        />
                      </Grid>

                      {/* Display Phone Number */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Display Phone Number
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body1" fontFamily="monospace">
                            {account.displayNumber || "—"}
                          </Typography>
                        </Stack>
                      </Grid>

                      {/* Number Quality */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Number Quality
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SignalCellularAltIcon
                            fontSize="small"
                            color="action"
                          />
                          <Chip
                            label={account.numberQuality || "UNKNOWN"}
                            color={getQualityColor(account.numberQuality)}
                            size="small"
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* Security & Integration */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <InfoSection
                    title="Security & Integration"
                    icon={<SecurityIcon />}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          WABA Token
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, bgcolor: "grey.50" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              wordBreak: "break-all",
                              fontSize: "0.75rem",
                            }}
                          >
                            {account.wabaToken}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Linked Partner
                        </Typography>
                        <Chip
                          label={account.partner}
                          variant="outlined"
                          color="primary"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS User ID
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {account.rcsOptionBUserId || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS API Key
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {account.rcsOptionBapiKey || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          RCS Bot ID
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {account.rcsBotId || "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </InfoSection>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right Column - Wallets & Actions */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Wallet Balances */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <InfoSection
                    title="Wallet Balances"
                    icon={<AccountBalanceWalletIcon />}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Main Wallet
                        </Typography>
                        <Chip
                          label={`₹${roundToMaxDecimals(
                            account?.wallet ?? 0,
                            2
                          )}`}
                          color="primary"
                          variant="filled"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          AI Wallet
                        </Typography>
                        <Chip
                          label={`${account?.aiWallet ?? 0}`}
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          RCS Wallet
                        </Typography>
                        <Chip
                          label={`${roundToMaxDecimals(
                            account?.rcsWalletRichMedia ?? 0,
                            2
                          )}`}
                          color="info"
                          variant="outlined"
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Voice Wallet
                        </Typography>
                        <Chip
                          label={account?.voiceCallWallet ?? 0}
                          color="info"
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Link
                          to={`/home/${id}/recharge-area`}
                          style={{ textDecoration: "none" }}
                        >
                          <GlassButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Add Credit
                          </GlassButton>
                        </Link>
                      </Box>
                      <Box sx={{ mt: 1 }}>
                        <Link
                          to={`/home/${id}/update-account-limit`}
                          style={{ textDecoration: "none" }}
                        >
                          <GlassButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Set Phone numbers Limit
                          </GlassButton>
                        </Link>
                      </Box>
                    </Stack>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* Integration Actions */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <InfoSection title="Integration Actions" icon={<LinkIcon />}>
                    <Stack spacing={2}>
                      <GlassButton
                        variant="contained"
                        fullWidth
                        startIcon={<FacebookIcon />}
                        onClick={() => {
                          launchEsSignup(true, account.customerId);
                        }}
                        sx={{
                          backgroundColor: "#1877f2",
                          "&:hover": {
                            backgroundColor: "#166fe5",
                          },
                        }}
                      >
                        Launch ES for Aisensy
                      </GlassButton>

                      <GlassButton
                        variant="contained"
                        fullWidth
                        startIcon={<FacebookIcon />}
                        onClick={() => {
                          launchEsSignup(false, account.customerId);
                        }}
                        sx={{
                          backgroundColor: "#1877f2",
                          "&:hover": {
                            backgroundColor: "#166fe5",
                          },
                        }}
                      >
                        Launch ES for Haptik
                      </GlassButton>

                      <GlassButton
                        variant="contained"
                        fullWidth
                        onClick={handleOpenRCSModal}
                        sx={{
                          backgroundColor: "#1877f2",
                          "&:hover": {
                            backgroundColor: "#166fe5",
                          },
                        }}
                      >
                        Configure RCS Option 2
                      </GlassButton>

                      {account?.partner === "haptik" &&
                        account?.onboardStatus === "success" && (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            Successfully onboarded to Haptik
                          </Alert>
                        )}

                      {account?.onboardStatus === "failed" && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          <Typography variant="subtitle2">
                            {account.hapTikError}
                          </Typography>
                          <Typography variant="body2">
                            {account.hapTikErrorDetails}
                          </Typography>
                        </Alert>
                      )}
                    </Stack>
                  </InfoSection>
                </CardContent>
              </Card>

              {/* Migration Tools */}
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <InfoSection title="Migration Tools">
                    <MigrateChatBot id={account.customerId} />
                  </InfoSection>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={openRCSModal} onClose={() => setOpenRCSModal(false)}>
        <DialogTitle>Configure RCS Option 2</DialogTitle>
        <DialogContent>
          <TextField
            label="User ID"
            fullWidth
            value={rcsUserId}
            onChange={(e) => setRcsUserId(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="API Key"
            fullWidth
            value={rcsApiKey}
            onChange={(e) => setRcsApiKey(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Bot ID"
            fullWidth
            value={rcsBotId}
            onChange={(e) => setRcsBotId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRCSModal(false)}>Cancel</Button>
          <LoadingButton
            loading={submitting}
            onClick={handleRCSSubmit}
            variant="contained"
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountDetails;
